var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { NotEnoughGas, FeeNotLoaded, InvalidAddress, ETHAddressNonEIP, RecipientRequired, AmountRequired, NotEnoughBalance, GasLessThanEstimate, PriorityFeeTooLow, } from "@ledgerhq/errors";
import { ethers } from "ethers";
import { findSubAccountById } from "@ledgerhq/coin-framework/account/index";
import { eip1559TransactionHasFees, getEstimatedFees, legacyTransactionHasFees } from "./logic";
import { NotEnoughNftOwned, NotOwnedNft, QuantityNeedsToBePositive } from "./errors";
// This regex will not work with Starknet since addresses are 65 caracters long after the 0x
const ethAddressRegEx = /^(0x)?[0-9a-fA-F]{40}$/;
/**
 * Validate an address for a transaction
 */
export const validateRecipient = (account, tx) => {
    const errors = {};
    const warnings = {};
    if (tx.recipient) {
        // Check if recipient is matching the format of a valid eth address or not
        const isRecipientMatchingEthFormat = tx.recipient.match(ethAddressRegEx);
        if (!isRecipientMatchingEthFormat) {
            errors.recipient = new InvalidAddress("", {
                currency: account.currency,
            });
        }
        else {
            // Check if address is respecting EIP-55
            try {
                const recipientChecksumed = ethers.utils.getAddress(tx.recipient);
                if (tx.recipient !== recipientChecksumed) {
                    // this case can happen if the user is entering an ICAP address.
                    throw new Error();
                }
            }
            catch (e) {
                // either getAddress throws for a bad checksum or we throw manually if the recipient isn't the same.
                warnings.recipient = new ETHAddressNonEIP(); // "Auto-verification not available: carefully verify the address"
            }
        }
    }
    else {
        errors.recipient = new RecipientRequired(); // ""
    }
    return [errors, warnings];
};
/**
 * Validate the amount of a transaction for an account
 */
export const validateAmount = (account, transaction, totalSpent) => {
    const errors = {};
    const warnings = {};
    const isTokenTransaction = (account === null || account === void 0 ? void 0 : account.type) === "TokenAccount";
    const isSmartContractInteraction = isTokenTransaction || transaction.data; // if the transaction is a smart contract interaction, it's normal that transaction has no amount
    const transactionHasFees = legacyTransactionHasFees(transaction) ||
        eip1559TransactionHasFees(transaction);
    const canHaveZeroAmount = isSmartContractInteraction && transactionHasFees;
    // if no amount or 0
    if ((!transaction.amount || transaction.amount.isZero()) && !canHaveZeroAmount) {
        errors.amount = new AmountRequired(); // "Amount required"
    }
    else if (totalSpent.isGreaterThan(account.balance)) {
        // if not enough to make the transaction
        errors.amount = new NotEnoughBalance(); // "Sorry, insufficient funds"
    }
    return [errors, warnings];
};
/**
 * Validate gas properties of a transaction, depending on its type and the account emitter
 */
export const validateGas = (account, tx, gasLimit, estimatedFees) => {
    const errors = {};
    const warnings = {};
    if (
    // if fees are not set or wrongly set
    !(legacyTransactionHasFees(tx) ||
        eip1559TransactionHasFees(tx))) {
        errors.gasPrice = new FeeNotLoaded(); // "Could not load fee rates. Please set manual fees"
    }
    else if (gasLimit.isZero()) {
        errors.gasLimit = new FeeNotLoaded(); // "Could not load fee rates. Please set manual fees"
    }
    else if (gasLimit.isLessThan(21000)) {
        // minimum gas for a tx is 21000
        errors.gasLimit = new GasLessThanEstimate(); // "This may be too low. Please increase"
    }
    else if (tx.recipient && estimatedFees.gt(account.balance)) {
        errors.gasPrice = new NotEnoughGas(); // "The parent account balance is insufficient for network fees"
    }
    else if (tx.maxPriorityFeePerGas && tx.maxPriorityFeePerGas.isZero()) {
        errors.maxPriorityFee = new PriorityFeeTooLow();
    }
    return [errors, warnings];
};
export const validateNft = (account, tx) => {
    var _a;
    if (!tx.nft)
        return [{}, {}];
    const errors = {};
    const warnings = {};
    const quantityIsPositive = tx.nft.quantity.gt(0);
    const nftFromAccount = (_a = account.nfts) === null || _a === void 0 ? void 0 : _a.find(nft => nft.tokenId === tx.nft.tokenId && nft.contract === tx.nft.contract);
    if (!nftFromAccount) {
        errors.amount = new NotOwnedNft();
    }
    else if (!quantityIsPositive) {
        errors.amount = new QuantityNeedsToBePositive();
    }
    else if (nftFromAccount.amount.lt(tx.nft.quantity)) {
        errors.amount = new NotEnoughNftOwned();
    }
    return [errors, warnings];
};
/**
 * Validate a transaction and get all possibles errors and warnings about it
 */
export const getTransactionStatus = (account, tx) => __awaiter(void 0, void 0, void 0, function* () {
    const subAccount = findSubAccountById(account, tx.subAccountId || "");
    const isTokenTransaction = (subAccount === null || subAccount === void 0 ? void 0 : subAccount.type) === "TokenAccount";
    const { gasLimit, additionalFees, amount } = tx;
    const estimatedFees = getEstimatedFees(tx);
    const totalFees = estimatedFees.plus(additionalFees || 0);
    const totalSpent = isTokenTransaction ? tx.amount : tx.amount.plus(totalFees);
    // Recipient related errors and warnings
    const [recipientErr, recipientWarn] = validateRecipient(account, tx);
    // Amount related errors and warnings
    const [amountErr, amountWarn] = validateAmount(subAccount || account, tx, totalSpent);
    // Gas related errors and warnings
    const [gasErr, gasWarn] = validateGas(account, tx, gasLimit, totalFees);
    const [nftErr, nftWarn] = validateNft(account, tx);
    const errors = Object.assign(Object.assign(Object.assign(Object.assign({}, recipientErr), gasErr), amountErr), nftErr);
    const warnings = Object.assign(Object.assign(Object.assign(Object.assign({}, recipientWarn), gasWarn), amountWarn), nftWarn);
    return {
        errors,
        warnings,
        estimatedFees,
        amount,
        totalSpent,
    };
});
export default getTransactionStatus;
//# sourceMappingURL=getTransactionStatus.js.map